/**
 * 목록 응답 정규화.
 *
 * 🔴 **실물 목록 응답이 세 꼴이다**(2026-09-17 실측 · `docs/api-survey.md` B-4).
 *
 * | 꼴                                      | 경로                                                        |
 * | --------------------------------------- | ----------------------------------------------------------- |
 * | `{items, total_count, limit, offset}`   | 나머지 전부(계약 `Page`)                                     |
 * | **벌거벗은 배열**                       | `/samples` · `/loads` · `/unloads` · `/exports` · `/reagents/lots` |
 * | **`{"items": []}` 만**                  | 지금은 없다 — 종전 `/resource-locks` 가 이 꼴이었고 v0.9.4 에서 경로째 걷혔다. 꼴은 계속 받는다 |
 *
 * → 한 꼴로 맞춘다. **총량을 지어내지 않는다** — 모르면 `null` 로 흘린다.
 *   (모르는 값을 추정으로 만들지 않는다는 규율. 화면이 「총 N건」을 못 쓰면 안 쓰는 게 맞다)
 * → 계약상 한 꼴이어야 맞다. 백엔드 보고 대상 M.
 */

export interface Listed<T> {
  items: T[]
  /** `null` = 총량 미상(서버가 안 줬다) */
  total_count: number | null
  /** 요청 에코. 서버가 안 주면 요청값을 그대로 쓴다 */
  limit: number | null
  offset: number | null
}

interface PageLike {
  items?: unknown
  total_count?: unknown
  limit?: unknown
  offset?: unknown
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function normalizeList<T>(
  raw: unknown,
  requested?: { limit?: number; offset?: number },
): Listed<T> {
  if (Array.isArray(raw)) {
    return {
      items: raw as T[],
      total_count: null,
      limit: requested?.limit ?? null,
      offset: requested?.offset ?? null,
    }
  }

  const page = (raw ?? {}) as PageLike
  return {
    items: Array.isArray(page.items) ? (page.items as T[]) : [],
    total_count: numberOrNull(page.total_count),
    limit: numberOrNull(page.limit) ?? requested?.limit ?? null,
    offset: numberOrNull(page.offset) ?? requested?.offset ?? null,
  }
}

/**
 * 가상 스크롤 끝 판정.
 *
 * ⚠ **`total_count` 가 없으면 이건 추론이다.** `/samples` 가 `limit`·`offset` 을 받으면서
 *   `total_count` 를 안 줘서(실측) 총량도 「마지막 페이지」도 알 수 없다. 서버가 정확히 `limit` 개를
 *   준 마지막 페이지에서 **빈 응답을 한 번 더 긁는다.** 허용 범위지만 깨끗하지 않다.
 *   → `total_count` 추가를 백엔드에 요청하는 것이 정답이다(보고 대상 J).
 */
export function hasMore<T>(page: Listed<T>): boolean {
  if (page.total_count !== null && page.offset !== null) {
    return page.offset + page.items.length < page.total_count
  }
  if (page.limit !== null) return page.items.length >= page.limit
  return false
}
